import type { EscPosEncoder } from "./escPosEncoder"

const TEMPLATE_TOKEN_PATTERN = /<(CB|L|N|CUT)>/g
const DEFAULT_HORIZONTAL_PADDING_DOTS = 8
const DEFAULT_BLACK_THRESHOLD = 180
const PURE_DIVIDER_PATTERN = /^\s*[-=_—·]{3,}\s*$/
const LABELED_DIVIDER_PATTERN = /^\s*[-=_—]{2,}\s*(.+?)\s*[-=_—]{2,}\s*$/

export type RasterReceiptTextRole = "title" | "body"
export type RasterReceiptAlignment = "LEFT" | "CENTER" | "RIGHT"

export interface RasterReceiptOptions {
  paperWidthDots: number
  titleFontSize: number
  bodyFontSize: number
  lineHeight: number
  fontFamily: string
  titleBold: boolean
  bodyBold: boolean
  blackThreshold?: number
}

export interface RasterReceiptLine {
  text: string
  role: RasterReceiptTextRole
  align: RasterReceiptAlignment
  bold?: boolean
  feedDots?: number
}

export interface RasterCanvasFactory {
  create: (width: number, height: number) => HTMLCanvasElement
}

interface RasterColumnToken {
  text: string
  startUnits: number
}

function createBrowserCanvas(width: number, height: number) {
  if (typeof document === "undefined") {
    throw new TypeError("当前环境不支持 Canvas 位图打印")
  }
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  return canvas
}

const browserCanvasFactory: RasterCanvasFactory = {
  create: createBrowserCanvas,
}

export function parseRasterReceiptLines(content: string): RasterReceiptLine[] {
  const lines: RasterReceiptLine[] = []
  let role: RasterReceiptTextRole = "body"
  let align: RasterReceiptAlignment = "LEFT"
  let cursor = 0
  let currentLine = ""

  const appendText = (value: string) => {
    const parts = value.split("\n")
    parts.forEach((part, index) => {
      currentLine += part
      if (index < parts.length - 1) {
        lines.push({ text: currentLine, role, align })
        currentLine = ""
      }
    })
  }

  for (const match of content.matchAll(TEMPLATE_TOKEN_PATTERN)) {
    const index = match.index ?? cursor
    appendText(content.slice(cursor, index))
    switch (match[1]) {
      case "CB":
        role = "title"
        align = "CENTER"
        break
      case "L":
        align = "LEFT"
        break
      case "N":
        role = "body"
        break
      case "CUT":
        break
    }
    cursor = index + match[0].length
  }
  appendText(content.slice(cursor))
  if (currentLine) {
    lines.push({ text: currentLine, role, align })
  }
  return lines
}

export function wrapRasterText(
  text: string,
  maxWidth: number,
  measureText: (value: string) => number,
) {
  if (!text) return [""]
  const lines: string[] = []
  let currentLine = ""
  for (const character of Array.from(text)) {
    const candidate = currentLine + character
    if (currentLine && measureText(candidate) > maxWidth) {
      lines.push(currentLine)
      currentLine = character
    } else {
      currentLine = candidate
    }
  }
  if (currentLine) lines.push(currentLine)
  return lines.length > 0 ? lines : [""]
}

export function imageDataToRasterBytes(
  imageData: ImageData,
  threshold = DEFAULT_BLACK_THRESHOLD,
) {
  const { data, width, height } = imageData
  const widthBytes = Math.ceil(width / 8)
  const raster = new Uint8Array(widthBytes * height)
  const normalizedThreshold = Math.max(0, Math.min(255, Math.round(threshold)))

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pixelIndex = (y * width + x) * 4
      const alpha = data[pixelIndex + 3]
      if (alpha === 0) continue
      const luminance = Math.round(
        data[pixelIndex] * 0.299
        + data[pixelIndex + 1] * 0.587
        + data[pixelIndex + 2] * 0.114,
      )
      if (luminance <= normalizedThreshold) {
        const byteIndex = y * widthBytes + Math.floor(x / 8)
        raster[byteIndex] |= 0x80 >> (x % 8)
      }
    }
  }
  return raster
}

function resolveFont(options: RasterReceiptOptions, role: RasterReceiptTextRole) {
  const fontSize = role === "title" ? options.titleFontSize : options.bodyFontSize
  const bold = role === "title" ? options.titleBold : options.bodyBold
  return {
    fontSize,
    bold,
    font: `${bold ? 700 : 400} ${fontSize}px ${options.fontFamily}`,
  }
}

function getCharacterUnits(character: string) {
  return character.charCodeAt(0) > 255 ? 2 : 1
}

export function parseRasterColumnTokens(text: string) {
  const tokens: RasterColumnToken[] = []
  let currentText = ""
  let currentStartUnits = 0
  let totalUnits = 0

  const flushToken = () => {
    if (!currentText) return
    tokens.push({ text: currentText, startUnits: currentStartUnits })
    currentText = ""
  }

  for (const character of Array.from(text)) {
    if (/\s/.test(character)) {
      flushToken()
    } else {
      if (!currentText) currentStartUnits = totalUnits
      currentText += character
    }
    totalUnits += getCharacterUnits(character)
  }
  flushToken()
  return { tokens, totalUnits }
}

function createDrawingContext(
  canvasFactory: RasterCanvasFactory,
  width: number,
  height: number,
) {
  const canvas = canvasFactory.create(width, height)
  const context = canvas.getContext("2d", { willReadFrequently: true })
  if (!context) {
    throw new Error("无法创建小票位图绘制上下文")
  }
  context.fillStyle = "#fff"
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.fillStyle = "#000"
  return { canvas, context }
}

function printDrawingCanvas(
  encoder: EscPosEncoder,
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  threshold?: number,
) {
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
  encoder
    .align("LEFT")
    .rasterImage(
      canvas.width,
      canvas.height,
      imageDataToRasterBytes(imageData, threshold),
    )
}

function renderDivider(
  encoder: EscPosEncoder,
  options: RasterReceiptOptions,
  canvasFactory: RasterCanvasFactory,
  label?: string,
) {
  const paperWidth = Math.max(128, Math.round(options.paperWidthDots))
  const fontSize = options.bodyFontSize
  const height = label
    ? Math.max(fontSize + 4, Math.round(fontSize * options.lineHeight))
    : Math.max(8, Math.round(fontSize * 0.55))
  const { canvas, context } = createDrawingContext(canvasFactory, paperWidth, height)
  const centerY = height / 2
  context.strokeStyle = "#000"
  context.lineWidth = 1
  if (!label) {
    context.beginPath()
    context.moveTo(DEFAULT_HORIZONTAL_PADDING_DOTS, centerY)
    context.lineTo(paperWidth - DEFAULT_HORIZONTAL_PADDING_DOTS, centerY)
    context.stroke()
    printDrawingCanvas(encoder, canvas, context, options.blackThreshold)
    return
  }

  context.font = `${options.bodyBold ? 700 : 400} ${fontSize}px ${options.fontFamily}`
  context.textAlign = "center"
  context.textBaseline = "middle"
  const labelWidth = context.measureText(label).width
  const lineGap = 8
  const lineEnd = Math.max(DEFAULT_HORIZONTAL_PADDING_DOTS, paperWidth / 2 - labelWidth / 2 - lineGap)
  context.beginPath()
  context.moveTo(DEFAULT_HORIZONTAL_PADDING_DOTS, centerY)
  context.lineTo(lineEnd, centerY)
  context.moveTo(paperWidth - lineEnd, centerY)
  context.lineTo(paperWidth - DEFAULT_HORIZONTAL_PADDING_DOTS, centerY)
  context.stroke()
  context.fillText(label, paperWidth / 2, centerY)
  printDrawingCanvas(encoder, canvas, context, options.blackThreshold)
}

function renderColumnLine(
  encoder: EscPosEncoder,
  line: RasterReceiptLine,
  options: RasterReceiptOptions,
  canvasFactory: RasterCanvasFactory,
  fontSize: number,
  font: string,
  lineHeight: number,
) {
  const paperWidth = Math.max(128, Math.round(options.paperWidthDots))
  const { tokens, totalUnits } = parseRasterColumnTokens(line.text)
  if (tokens.length < 2 || totalUnits <= 0) return false

  const { canvas, context } = createDrawingContext(canvasFactory, paperWidth, lineHeight)
  const contentWidth = paperWidth - DEFAULT_HORIZONTAL_PADDING_DOTS * 2
  context.font = font
  context.textAlign = "left"
  context.textBaseline = "middle"
  tokens.forEach((token, index) => {
    const nextStart = tokens[index + 1]?.startUnits ?? totalUnits
    const tokenUnits = Math.max(1, nextStart - token.startUnits)
    const x = DEFAULT_HORIZONTAL_PADDING_DOTS + token.startUnits / totalUnits * contentWidth
    const maxWidth = Math.max(fontSize / 2, tokenUnits / totalUnits * contentWidth)
    context.fillText(token.text, x, lineHeight / 2, maxWidth)
  })
  printDrawingCanvas(encoder, canvas, context, options.blackThreshold)
  return true
}

function renderLine(
  encoder: EscPosEncoder,
  line: RasterReceiptLine,
  options: RasterReceiptOptions,
  canvasFactory: RasterCanvasFactory,
) {
  const paperWidth = Math.max(128, Math.round(options.paperWidthDots))
  if (typeof line.feedDots === "number") {
    encoder.feedDots(line.feedDots)
    return
  }
  if (PURE_DIVIDER_PATTERN.test(line.text)) {
    renderDivider(encoder, options, canvasFactory)
    return
  }
  const labeledDivider = line.text.match(LABELED_DIVIDER_PATTERN)?.[1]
  if (labeledDivider) {
    renderDivider(encoder, options, canvasFactory, labeledDivider)
    return
  }

  const { fontSize, bold } = resolveFont(options, line.role)
  const lineHeight = Math.max(fontSize + 2, Math.round(fontSize * options.lineHeight))
  if (!line.text) {
    encoder.feedDots(lineHeight)
    return
  }

  const measuringCanvas = canvasFactory.create(paperWidth, lineHeight)
  const measuringContext = measuringCanvas.getContext("2d")
  if (!measuringContext) {
    throw new Error("无法创建小票位图绘制上下文")
  }
  const isColumnLine = /\S\s{2,}\S/.test(line.text)
  const fontFamily = options.fontFamily
  const font = `${(line.bold ?? bold) ? 700 : 400} ${fontSize}px ${fontFamily}`
  if (isColumnLine && renderColumnLine(
    encoder,
    line,
    options,
    canvasFactory,
    fontSize,
    font,
    lineHeight,
  )) {
    return
  }
  measuringContext.font = font
  const maxTextWidth = paperWidth - DEFAULT_HORIZONTAL_PADDING_DOTS * 2
  const wrappedLines = wrapRasterText(
    line.text,
    maxTextWidth,
    value => measuringContext.measureText(value).width,
  )

  const { canvas, context } = createDrawingContext(
    canvasFactory,
    paperWidth,
    lineHeight * wrappedLines.length,
  )
  context.font = font
  context.textBaseline = "middle"
  context.textAlign = line.align === "CENTER" ? "center" : line.align === "RIGHT" ? "right" : "left"
  const x = line.align === "CENTER"
    ? paperWidth / 2
    : line.align === "RIGHT"
      ? paperWidth - DEFAULT_HORIZONTAL_PADDING_DOTS
      : DEFAULT_HORIZONTAL_PADDING_DOTS
  wrappedLines.forEach((wrappedLine, index) => {
    context.fillText(wrappedLine, x, lineHeight * index + lineHeight / 2)
  })

  printDrawingCanvas(encoder, canvas, context, options.blackThreshold)
}

export function printRasterPlainTextReceipt(
  encoder: EscPosEncoder,
  content: string,
  options: RasterReceiptOptions,
  canvasFactory: RasterCanvasFactory = browserCanvasFactory,
) {
  const lines = parseRasterReceiptLines(content)
  lines.forEach(line => renderLine(encoder, line, options, canvasFactory))
}

export class ReceiptRasterCommandRecorder {
  private readonly lines: RasterReceiptLine[] = []
  private currentText = ""
  private currentRole: RasterReceiptTextRole = "body"
  private currentAlign: RasterReceiptAlignment = "LEFT"
  private currentBold = false
  private cutRequested = false

  initialize() {
    this.lines.length = 0
    this.currentText = ""
    this.currentRole = "body"
    this.currentAlign = "LEFT"
    this.currentBold = false
    this.cutRequested = false
    return this
  }

  textRole(role: RasterReceiptTextRole) {
    this.currentRole = role
    return this
  }

  text(content: string) {
    const parts = content.split("\n")
    parts.forEach((part, index) => {
      this.currentText += part
      if (index < parts.length - 1) this.newline()
    })
    return this
  }

  align(align: RasterReceiptAlignment) {
    this.currentAlign = align
    return this
  }

  size(_width: number, _height: number) {
    return this
  }

  lineSpacing(_dots: number) {
    return this
  }

  lineFeedGap(_dots: number) {
    return this
  }

  bold(active: boolean) {
    this.currentBold = active
    return this
  }

  newline() {
    this.lines.push({
      text: this.currentText,
      role: this.currentRole,
      align: this.currentAlign,
      bold: this.currentBold,
    })
    this.currentText = ""
    return this
  }

  feedDots(dots: number) {
    if (this.currentText) this.newline()
    this.lines.push({
      text: "",
      role: this.currentRole,
      align: this.currentAlign,
      feedDots: dots,
    })
    return this
  }

  rasterImage(_widthDots: number, _heightDots: number, _data: Uint8Array) {
    throw new TypeError("位图记录器不接受嵌套光栅数据")
  }

  cut() {
    if (this.currentText) this.newline()
    this.cutRequested = true
    return this
  }

  renderTo(
    encoder: EscPosEncoder,
    options: RasterReceiptOptions,
    canvasFactory: RasterCanvasFactory = browserCanvasFactory,
  ) {
    if (this.currentText) this.newline()
    encoder.initialize()
    this.lines.forEach(line => renderLine(encoder, line, options, canvasFactory))
    if (this.cutRequested) encoder.cut()
  }
}
